import { Injectable } from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import { Location } from '@angular/common';

// The query params that carry a position in the configurator from one module to the next
const CONFIGURATOR_CONTEXT_PARAMS = ['part', 'item', 'object', 'cuttingList'] as const;
type ConfiguratorContextParam = typeof CONFIGURATOR_CONTEXT_PARAMS[number];

export interface ConfiguratorPlace {
	projectId: string;
	path: string;
	queryParams: Params;
}

@Injectable({
	providedIn: 'root'
})
export class RouteUtilsService {
	// The configurator writes its selection (part, item, cutting list) into the address bar
	// silently, past the router, so router.url lags behind it. Where the user is in the
	// configurator is therefore read from the address bar.
	private lastConfigurator: ConfiguratorPlace | null = null;

	constructor(
		private router: Router,
		private location: Location
	) {
		this.location.onUrlChange(() => {
			this.lastConfigurator = this.configuratorPlace(this.location.path()) ?? this.lastConfigurator;
		});
	}

	/** The place in the project's configurator the user last was, if they were in it. */
	lastConfiguratorPlace(projectId: string): ConfiguratorPlace | null {
		return this.lastConfigurator?.projectId === projectId ? this.lastConfigurator : null;
	}

	private configuratorPlace(url: string): ConfiguratorPlace | null {
		const tree = this.router.parseUrl(url);
		const segments = tree.root.children.primary?.segments.map((segment) => segment.path) ?? [];
		return segments[0] === 'projects' && segments[2] === 'configurator'
			? { projectId: segments[1], path: '/' + segments.join('/'), queryParams: tree.queryParams }
			: null;
	}

	/**
	 * Updates query parameters silently without triggering router events
	 * @param route - The activated route to get current params from
	 * @param newParams - Object with new query parameters to set
	 * @param preserveExisting - Whether to preserve existing query params (default: true)
	 */
	updateQueryParamsSilently(
		route: ActivatedRoute,
		newParams: Record<string, string>,
		preserveExisting: boolean = true
	): void {
		let queryParams: Record<string, any> = {};

		if (preserveExisting) {
			queryParams = { ...route.snapshot.queryParams };
		}

		// Merge new params, filtering out null/undefined values
		Object.entries(newParams).forEach(([key, value]) => {
			if (value !== null && value !== undefined) {
				queryParams[key] = value;
			} else {
				delete queryParams[key];
			}
		});

		const path = this.router.url.split('?')[0];
		const queryString = new URLSearchParams(queryParams).toString();
		const newUrl = queryString ? `${path}?${queryString}` : path;
		this.location.replaceState(newUrl);
	}

	updateConfiguratorQueryParamsSilently(
		route: ActivatedRoute,
		params: { part?: string; item?: string; object?: string; cuttingList?: string }
	): void {
		const queryParams = {
			part: params.part || route.snapshot.queryParams.part || null,
			item: params.item  || null,
			// A cutting list of the part shown instead of its scene
			cuttingList: params.cuttingList || null,
		    ...(params.object ? {object: params.object || null} : {})
		};

		this.updateQueryParamsSilently(route, queryParams, false);
	}

	/**
	 * Opens a module inside the configurator (materials, quotation). The link carries where the
	 * user was (the configurator sub-view and its part/item/cutting list), so
	 * returnToConfigurator can bring them back to exactly that place. Opened again from inside
	 * the module, it keeps the place it was opened from.
	 */
	openConfiguratorModule(module: string): void {
		const url = this.location.path();
		const tree = this.router.parseUrl(url);
		const segments = tree.root.children.primary?.segments.map((segment) => segment.path) ?? [];
		const configuratorIndex = segments.indexOf('configurator');
		if (configuratorIndex < 0) {
			throw new Error(`The ${module} module can only be opened from the configurator, not from ${url}`);
		}
		const subPath = segments.slice(configuratorIndex + 1);
		const view = subPath[0] === module ? tree.queryParams.view : subPath.join('/');
		this.router.navigate([...segments.slice(0, configuratorIndex + 1), module], {
			queryParams: { ...this.configuratorContext(tree.queryParams), ...(view ? { view } : {}) },
		});
	}

	/**
	 * Leaves a module for the configurator view and selection it was opened from. `context`
	 * replaces part of that selection, e.g. the item the module just created.
	 */
	returnToConfigurator(route: ActivatedRoute, context: Partial<Record<ConfiguratorContextParam, string>> = {}): void {
		const queryParams = route.snapshot.queryParams;
		const segments = this.router.parseUrl(this.router.url).root.children.primary.segments.map((segment) => segment.path);
		const configuratorPath = segments.slice(0, segments.indexOf('configurator') + 1);
		this.router.navigate([...configuratorPath, ...(queryParams.view ? queryParams.view.split('/') : [])], {
			queryParams: { ...this.configuratorContext(queryParams), ...context },
		});
	}

	/** The query params that say where in the configurator the user is. */
	configuratorContext(queryParams: Params): Partial<Record<ConfiguratorContextParam, string>> {
		return Object.fromEntries(
			CONFIGURATOR_CONTEXT_PARAMS
				.filter((param) => queryParams[param])
				.map((param) => [param, queryParams[param]])
		);
	}

	updateCuttingListQueryParamsSilently(
		route: ActivatedRoute,
		params: { cuttingList: string }
	): void {
		const queryParams = {
			cuttingList: params.cuttingList || route.snapshot.queryParams.cuttingList || null
		};

		this.updateQueryParamsSilently(route, queryParams, false);
	}

	/**
	 * The redirectTo for a side trip (account, users, password) opened from the current page:
	 * the current page itself, or, from inside another side trip, the place that one returns to.
	 */
	sideTripRedirectTo(): string | undefined {
		const url = this.location.path();
		const tree = this.router.parseUrl(url);
		const firstSegment = tree.root.children.primary?.segments[0]?.path;
		return firstSegment === 'auth' || firstSegment === 'users'
			? tree.queryParams.redirectTo
			: url;
	}

	/** Where a side trip sends the user back to: the in-app redirectTo it was opened with, else the fallback. */
	sideTripReturnUrl(route: ActivatedRoute, fallback: string): string {
		const redirectTo = route.snapshot.queryParamMap.get('redirectTo');
		return redirectTo?.startsWith('/') && !redirectTo.startsWith('//') ? redirectTo : fallback;
	}
}
